import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Filter, Download, ExternalLink, MapPin, Phone, CheckCircle, MessageCircle, Loader2, Droplets, Home, Calculator, X, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ParcelMapModal from '../components/ParcelMapModal';
import LotCard from '../components/LotCard';
import FeaturedSpecialListing from '../components/FeaturedSpecialListing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Unit utility information
const unitUtilities = {
  // City Water & Sewer
  '5': { water: true, sewer: true },
  '7': { water: true, sewer: true },
  '8': { water: true, sewer: true },
  '9': { water: true, sewer: true },
  '38': { water: true, sewer: true },
  // City Water Only
  '10': { water: true, sewer: false },
  '11': { water: true, sewer: false },
  '12': { water: true, sewer: false },
  '16': { water: true, sewer: false },
  '21': { water: true, sewer: false },
  '28': { water: true, sewer: false },
  '31': { water: true, sewer: false },
  '42': { water: true, sewer: false },
  '44': { water: true, sewer: false },
  '46': { water: true, sewer: false },
  '48': { water: true, sewer: false },
  '50': { water: true, sewer: false },
};

// Helper to get utility type for a lot.
// Tags take precedence over unit-based rules (used for exceptions like Country Club, Foundation Park, etc.)
const getUtilityType = (item) => {
  // Explicit Water/Sewer chosen in the admin form takes precedence over unit/tags.
  const w = String(item.water || '').toLowerCase();
  const s = String(item.sewer || '').toLowerCase();
  if (w.includes('city') && s.includes('sewer')) return 'water_sewer';
  if (w.includes('city')) return 'water_only';
  if (w.includes('well') || s.includes('septic')) return 'well_septic';

  const tags = (item.tags || []).map(t => String(t).toLowerCase());
  if (tags.includes('city-water-sewer') || tags.includes('water-sewer')) return 'water_sewer';
  if (tags.includes('city-water-only') || tags.includes('water-only')) return 'water_only';
  if (tags.includes('well-septic')) return 'well_septic';
  // Fallback: unit-based lookup (covers the bulk of Port Malabar lots)
  const info = unitUtilities[item.unit];
  if (info?.water && info?.sewer) return 'water_sewer';
  if (info?.water) return 'water_only';
  return 'well_septic';
};

// ===== Lot price calculator logic =====
// Base $/sqft for the first 10,000 sqft, by unit.
const UNIT_RATE = (() => {
  const map = {};
  ['49'].forEach(u => (map[u] = 4.10));
  ['15','17','18','19','20','22','23','24','25','30','32','36','37','10','11','12','16','21','28','31','46'].forEach(u => (map[u] = 4.50));
  ['13','14','26','39','41','42','44','50','48','5','7','8','9','38'].forEach(u => (map[u] = 5.20));
  return map;
})();

const NOT_INDIVIDUAL_UNITS = ['51','52','53']; // package only
const CALL_UNITS = ['40'];                      // commercial / multifamily — priced individually

const hasLetterBlock = (block) => /[A-Za-z]/.test(String(block || ''));
const parseAcres = (a) => {
  const n = parseFloat(String(a || '').replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : n;
};
// A blank zoning/FLU means a standard single-family residential lot.
const effZoning = (item) => (item && item.zoning && String(item.zoning).trim()) ? String(item.zoning).trim() : 'Residential';
const effFlu = (item) => (item && item.flu && String(item.flu).trim()) ? String(item.flu).trim() : 'Residential';
// Numeric value of a unit/block/lot for low-to-high sorting (non-numeric sinks to the end).
const numVal = (v) => { const n = parseInt(String(v || '').replace(/[^0-9]/g, ''), 10); return isNaN(n) ? 999999 : n; };
// Parse a hand-set dollar price like "$75,000". Returns 0 if not a real number
// (e.g. "Contact for Price"), so the calculator is used instead.
const parsePrice = (p) => {
  const n = parseFloat(String(p || '').replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : n;
};
const monthlyPayment = (financed) => Math.round((financed * 13.22) / 1000);

const financingFor = (price) => ({
  status: 'price',
  price,
  down30: Math.round(price * 0.30),
  fin30: Math.round(price * 0.70),
  monthly30: monthlyPayment(price * 0.70),
  down40: Math.round(price * 0.40),
  fin40: Math.round(price * 0.60),
  monthly40: monthlyPayment(price * 0.60),
});

// Returns pricing info for a lot.
// status: 'price' (show calculator), 'call' (Call for Pricing), 'package' (not sold individually)
const getLotPricing = (item, utilityType, canal) => {
  const unit = String(item.unit || '');
  const cashOnly = !!item.cashOnly; // listings we market but don't own — no owner financing

  // A hand-set price in admin (Price field) overrides the calculator entirely.
  const explicit = parsePrice(item.price);
  if (explicit > 0) {
    return { ...financingFor(explicit), fixed: true, cashOnly };
  }

  if (NOT_INDIVIDUAL_UNITS.includes(unit)) return { status: 'package' };
  if (CALL_UNITS.includes(unit) || hasLetterBlock(item.block)) return { status: 'call' };

  const rate = UNIT_RATE[unit];
  const acres = parseAcres(item.acres);
  if (!rate || !acres) return { status: 'call' };

  const sqft = acres * 43560;
  let price = Math.min(sqft, 10000) * rate;
  if (sqft > 10000) price += (sqft - 10000) * 3;
  if (utilityType === 'water_only') price += 20000;
  if (utilityType === 'water_sewer') price += 40000;
  if (canal) price += 5000;
  price = Math.round(price);

  return { ...financingFor(price), cashOnly };
};

const usd = (n) => '$' + Math.round(n).toLocaleString('en-US');

// Fallback featured properties (used only if database is empty)
const fallbackFeatured = [
  {
    id: '328-malabar',
    address: '328 Malabar Rd',
    city: 'Palm Bay, FL',
    price: '$1,575,000',
    acres: '1.75 AC',
    tag: 'Signalized Corner',
    tagColor: 'bg-green-600',
    why: 'Signalized corner on Malabar Rd — utilities in place, strong traffic counts.',
    link: '/listing/328-malabar-rd'
  },
  {
    id: 'babcock-18ac',
    address: '6950 Babcock St SE',
    city: 'Palm Bay, FL',
    price: '$4,950,000',
    acres: '18 AC',
    tag: 'Large Development',
    tagColor: 'bg-purple-600',
    why: 'Major development opportunity — commercial, multifamily, or mixed-use potential.'
  },
  {
    id: 'thor-mf',
    address: '451 Thor Ave SE',
    city: 'Palm Bay, FL',
    price: '$328,500',
    acres: '0.73 AC',
    tag: 'MF-20 Zoned',
    tagColor: 'bg-blue-600',
    why: 'Multi-family zoned corner — approved for 14+ units, strong rental demand.'
  }
];

// Tag color mapping
const tagColorMap = {
  'Commercial': 'bg-blue-600',
  'Multi-Family': 'bg-purple-600',
  'Residential': 'bg-amber-600',
  'Assemblage': 'bg-red-600',
};

// Buyer favorites — saved in the visitor's own browser (no login).
const FAV_KEY = 'pbll_favorites';
const favId = (item) => item.id || item.inventoryId || `${item.unit}-${item.block}-${item.lot}`;
const readFavorites = () => {
  try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || '[]')); }
  catch (e) { return new Set(); }
};

// Extract quadrant (NW, NE, SW, SE) from street name
const extractQuadrant = (streetName) => {
  const str = String(streetName || '').toUpperCase();
  if (str.includes(' NW')) return 'NW';
  if (str.includes(' NE')) return 'NE';
  if (str.includes(' SW')) return 'SW';
  if (str.includes(' SE')) return 'SE';
  return null;
};

const Inventory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUnit, setFilterUnit] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [minAcres, setMinAcres] = useState('');
  const [maxAcres, setMaxAcres] = useState('');
  const [zoningFilter, setZoningFilter] = useState([]); // selected zoning values
  const [fluFilter, setFluFilter] = useState([]);        // selected FLU values
  const [quadrantFilter, setQuadrantFilter] = useState([]); // selected quadrants: NW, NE, SW, SE
  const [sortBy, setSortBy] = useState('unit');          // unit | block | lot | sizeAsc | sizeDesc
  const [showZoning, setShowZoning] = useState(true);
  const [showFlu, setShowFlu] = useState(false);
  const [showQuadrant, setShowQuadrant] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [priceItem, setPriceItem] = useState(null); // lot whose price popup is open
  const [canal, setCanal] = useState(false);
  const [downPct, setDownPct] = useState(30); // custom down-payment %
  const [mapItem, setMapItem] = useState(null); // lot whose satellite map is open
  const [showUnitMap, setShowUnitMap] = useState(false); // Palm Bay unit map lightbox
  const [favorites, setFavorites] = useState(() => readFavorites()); // saved lot ids
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [searchParams] = useSearchParams();

  // Returning from a lot's detail page with ?lot=<id> → scroll to that card and
  // briefly highlight it, so the visitor lands back on the exact lot they viewed.
  useEffect(() => {
    const want = searchParams.get('lot');
    if (!want || loading) return;
    const el = document.getElementById(`lot-${want}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-2', 'ring-amber-500');
    const t = setTimeout(() => el.classList.remove('ring-2', 'ring-amber-500'), 2500);
    return () => clearTimeout(t);
  }, [loading, inventory, searchParams]);

  const toggleFavorite = (item) => {
    setFavorites(prev => {
      const next = new Set(prev);
      const id = favId(item);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(FAV_KEY, JSON.stringify([...next])); } catch (e) {}
      return next;
    });
  };

  // One lot as a photo card. Favorites + handlers come from this page so the
  // saved (red-heart) state stays in sync across cards, filter, and reloads.
  const renderLotCard = (item, accent, utilityLabel) => (
    <LotCard
      key={item.id || item.inventoryId}
      item={item}
      favorited={favorites.has(favId(item))}
      onToggleFav={toggleFavorite}
      onSeePrice={openPriceModal}
      onOpenMap={setMapItem}
      utilityLabel={utilityLabel}
      accent={accent}
    />
  );

  const openPriceModal = (item) => { setCanal(false); setDownPct(30); setPriceItem(item); };
  const closePriceModal = () => setPriceItem(null);

  // Load featured properties from database
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/properties/featured`);
        if (response.data && response.data.length > 0) {
          // Transform database properties to featured format
          const dbFeatured = response.data.map(prop => ({
            id: prop.id,
            address: prop.title,
            city: prop.city,
            price: prop.price,
            acres: prop.acres,
            tag: prop.tags?.[0] || prop.propertyType,
            tagColor: tagColorMap[prop.propertyType] || 'bg-slate-600',
            why: prop.description || '',
            link: prop.featured ? `/listing/${prop.id}` : null
          }));
          setFeaturedProperties(dbFeatured);
        } else {
          // Use fallback if database is empty
          setFeaturedProperties(fallbackFeatured);
        }
      } catch (error) {
        console.error('Error fetching featured properties:', error);
        setFeaturedProperties(fallbackFeatured);
      } finally {
        setFeaturedLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  // Load inventory from database API
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        // Use dedicated inventory endpoint — only returns lots, never curated listings
        const response = await axios.get(`${API}/properties/inventory`);
        if (response.data && response.data.length > 0) {
          setInventory(response.data);
        }
      } catch (error) {
        console.error('Error loading inventory:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInventory();
  }, []);

  // Pin the table column headers exactly below the sticky site header,
  // measuring its real height so they never hide behind the logo bar.
  useEffect(() => {
    const setOffset = () => {
      const header = document.querySelector('[data-testid="site-header"]');
      const h = header ? header.offsetHeight : 150;
      document.documentElement.style.setProperty('--inv-header-top', `${h}px`);
    };
    setOffset();
    window.addEventListener('resize', setOffset);
    return () => window.removeEventListener('resize', setOffset);
  }, []);

  // Get unique units for filter
  const uniqueUnits = useMemo(() => {
    const units = [...new Set(inventory.map(item => item.unit))].filter(u => u);
    return units.sort((a, b) => {
      const numA = parseInt(a) || 999;
      const numB = parseInt(b) || 999;
      return numA - numB;
    });
  }, [inventory]);

  // Zoning / FLU / Quadrant checkbox options with live counts
  const zoningOptions = useMemo(() => {
    const counts = {};
    inventory.forEach(i => { const z = effZoning(i); counts[z] = (counts[z] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [inventory]);
  const fluOptions = useMemo(() => {
    const counts = {};
    inventory.forEach(i => { const f = effFlu(i); counts[f] = (counts[f] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [inventory]);
  const quadrantOptions = useMemo(() => {
    const counts = { 'NW': 0, 'NE': 0, 'SW': 0, 'SE': 0 };
    inventory.forEach(i => {
      const q = extractQuadrant(i.streetName);
      if (q && counts[q] !== undefined) counts[q]++;
    });
    return Object.entries(counts).filter(([_, count]) => count > 0).sort((a, b) => b[1] - a[1]);
  }, [inventory]);

  const toggleValue = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);

  // Filter inventory
  const filteredInventory = useMemo(() => {
    const min = parseFloat(minAcres);
    const max = parseFloat(maxAcres);
    return inventory.filter(item => {
      const matchesSearch =
        (item.inventoryId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.unit.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.block.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.lot.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.streetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${item.streetNumber} ${item.streetName}`.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesUnit = filterUnit === 'all' || item.unit === filterUnit;
      const matchesFav = !favoritesOnly || favorites.has(favId(item));

      const ac = parseAcres(item.acres);
      const matchesMin = isNaN(min) || ac >= min;
      const matchesMax = isNaN(max) || ac <= max;
      const matchesZoning = zoningFilter.length === 0 || zoningFilter.includes(effZoning(item));
      const matchesFlu = fluFilter.length === 0 || fluFilter.includes(effFlu(item));
      const matchesQuadrant = quadrantFilter.length === 0 || quadrantFilter.includes(extractQuadrant(item.streetName));

      return matchesSearch && matchesUnit && matchesFav && matchesMin && matchesMax && matchesZoning && matchesFlu && matchesQuadrant;
    });
  }, [inventory, searchTerm, filterUnit, favoritesOnly, favorites, minAcres, maxAcres, zoningFilter, fluFilter, quadrantFilter]);

  // Group inventory by utility type and sort by unit number
  const groupedInventory = useMemo(() => {
    const waterSewer = [];
    const waterOnly = [];
    const wellSeptic = [];

    filteredInventory.forEach(item => {
      const utilityType = getUtilityType(item);
      if (utilityType === 'water_sewer') {
        waterSewer.push(item);
      } else if (utilityType === 'water_only') {
        waterOnly.push(item);
      } else {
        wellSeptic.push(item);
      }
    });

    // Sort each group by the chosen field (low-to-high for unit/block/lot).
    const comparator = (a, b) => {
      switch (sortBy) {
        case 'block': return numVal(a.block) - numVal(b.block);
        case 'lot': return numVal(a.lot) - numVal(b.lot);
        case 'sizeAsc': return parseAcres(a.acres) - parseAcres(b.acres);
        case 'sizeDesc': return parseAcres(b.acres) - parseAcres(a.acres);
        default: return numVal(a.unit) - numVal(b.unit);
      }
    };

    waterSewer.sort(comparator);
    waterOnly.sort(comparator);
    wellSeptic.sort(comparator);

    return { waterSewer, waterOnly, wellSeptic };
  }, [filteredInventory, sortBy]);

  // Download as Excel (CSV)
  const downloadExcel = () => {
    const csvHeader = 'Inventory ID,County,Owner,Acres,Unit,Block,Lot,Street Number,Street Name,City,Tax Account,Property Appraiser Link\n';
    const csvContent = filteredInventory.map(item => {
      const link = item.taxAccount 
        ? `https://www.bcpao.us/map/?r=${item.taxAccount}`
        : 'https://www.bcpao.us/PropertySearch/#/nav/Search';
      return `"${item.inventoryId}","${item.county}","${item.owner}","${item.acres}","${item.unit}","${item.block}","${item.lot}","${item.streetNumber}","${item.streetName}","${item.city}","${item.taxAccount}","${link}"`;
    }).join('\n');

    const blob = new Blob([csvHeader + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `palm_bay_inventory_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get property appraiser account link
  const getPropertyAppraiserLink = (item) => {
    if (item.taxAccount) {
      return `https://www.bcpao.us/map/?r=${item.taxAccount}`;
    }
    return `https://www.bcpao.us/PropertySearch/#/nav/Search`;
  };

  // Get Google Maps search link from address
  const getGoogleMapsLink = (item) => {
    const q = [item.streetNumber, item.streetName, item.city, 'FL'].filter(Boolean).join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-4" />
          <div className="text-xl font-semibold text-slate-700">Loading inventory...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 relative">
      <Helmet>
        <title>Lots for Sale in Palm Bay FL | Owner Financing Available</title>
        <meta name="description" content="Browse 580+ residential lots in Palm Bay, Florida. Filter by location, size, and utility availability. Owner financing on most lots. Starting at $41,000." />
        <meta name="keywords" content="lots for sale Palm Bay FL, vacant land Palm Bay, Palm Bay residential lots, owner financing land Florida" />
        <link rel="canonical" href="https://palmbaylots-land.com/inventory" />
        <meta property="og:title" content="Lots for Sale in Palm Bay FL | Owner Financing Available" />
        <meta property="og:description" content="Browse 580+ residential lots for sale in Palm Bay, FL. Owner financing available — no banks, no hassle." />
        <meta property="og:url" content="https://palmbaylots-land.com/inventory" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_palmbayhomes/artifacts/am09bmq5_Untitled.png" />
        {/* ItemList schema — lot categories available */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Palm Bay Residential Lot Inventory',
          description: 'Available residential lots for sale in Palm Bay, FL with owner financing.',
          url: 'https://palmbaylots-land.com/inventory',
          numberOfItems: inventory.length,
          dateModified: new Date().toISOString().split('T')[0],
          provider: {
            '@type': 'RealEstateAgent',
            name: 'Vahid Reza Rajabian',
            telephone: '321-333-7230',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '1663 Georgia St NE Suite 700',
              addressLocality: 'Palm Bay',
              addressRegion: 'FL',
              postalCode: '32907',
              addressCountry: 'US'
            }
          },
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Quarter-acre buildable residential lots', url: 'https://palmbaylots-land.com/inventory' },
            { '@type': 'ListItem', position: 2, name: 'Oversized & corner lots', url: 'https://palmbaylots-land.com/inventory' },
            { '@type': 'ListItem', position: 3, name: 'Bulk and assemblage residential packages', url: 'https://palmbaylots-land.com/inventory' },
            { '@type': 'ListItem', position: 4, name: 'Lots with city water & sewer', url: 'https://palmbaylots-land.com/inventory' },
            { '@type': 'ListItem', position: 5, name: 'Well & septic compatible lots', url: 'https://palmbaylots-land.com/inventory' }
          ]
        })}</script>
      </Helmet>
      {/* ===== STEP 1: Hero Section with Authority ===== */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-14 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400 mb-4">
              Palm Bay Land Inventory — Curated, Not Scraped
            </h1>
            <p className="text-base md:text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
              Residential, commercial, multifamily, and development-ready parcels in Palm Bay.<br />
              If you don't know what zoning actually works, I'll tell you before you waste time or money.
            </p>
            
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-lg font-bold transition-colors shadow-lg"
            >
              <Phone className="w-5 h-5" />
              Talk to a Palm Bay Land Expert
            </Link>
            
            <p className="text-sm text-gray-500 mt-4">
              <Link to="/contact" className="text-amber-400 hover:text-amber-300 underline">
                Request Parcel & Zoning Review
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* ===== Map Search Bar ===== */}
      <section className="bg-amber-50 border-b border-amber-200 py-6">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <div>
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 justify-center sm:justify-start">
                <MapPin className="w-6 h-6 text-amber-600" />
                Search Our Lots on the Map
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                See every lot by location. The map opens in a new window — close the map window to come back to this inventory page.
              </p>
            </div>
            <a
              href="https://www.palmbayland.com/service-search/Search-Map.php"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold transition-colors shadow-md"
            >
              <MapPin className="w-5 h-5" />
              Search by Map
            </a>
          </div>
        </div>
      </section>

      {/* ===== SEO Intro / Keyword Content ===== */}
      <section className="py-10 bg-white border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Lots for Sale in Palm Bay, FL
            </h2>
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                Browse our up-to-date list of <strong>residential lots for sale in Palm Bay, FL</strong>. We focus exclusively on <strong>vacant land in Palm Bay</strong> — single-family buildable parcels, quarter-acre lots, oversized lots, corner lots, and assemblage opportunities throughout Brevard County. Whether you are looking to build your first home, hold land as a long-term investment, or assemble a builder package, you will find <strong>Palm Bay residential lots</strong> here at honest, real-market pricing.
              </p>
              <p>
                Every lot in our inventory is offered with <strong>owner financing</strong> — no bank required and no long, drawn-out mortgage process. We may check credit, but qualifying is simple: we are not turning people away over a low credit score, a past bankruptcy, or a divorce. There is <strong>no appraisal and no weeks-long wait</strong> — in most cases we can set up your financing the same day, right in our office. As long as you can make the payments, we can usually get you approved. Our standard terms start at <strong>30% down with the deed transferring once 40% is paid</strong>, with the balance amortized over a fixed term. We have closed thousands of transactions on these exact terms since 2003.
              </p>
              <p>
                Each listing below shows the inventory ID, unit/block/lot, acreage, street address, and a direct link to the Brevard County Property Appraiser so you can verify zoning, utilities, and tax assessment before you call. If you already know what you are looking for — call or text us at <a href="tel:3213337230" className="text-amber-600 font-semibold hover:underline">321-333-7230</a> and we will match you to the right parcel. If you are still figuring out what works for your build, we are happy to walk you through it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured spotlight lot — 1039 Hooper Ave NE */}
      <FeaturedSpecialListing />

      {/* ===== STEP 2: Featured / Best Opportunities ===== */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Best Opportunities Right Now
              </h2>
              <p className="text-slate-600">
                Hand-picked parcels with real demand, visibility, or development upside.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredProperties.map((prop) => (
                <div key={prop.id} className="bg-slate-50 border border-slate-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
                  <div className="mb-3">
                    <span className={`inline-block px-3 py-1 rounded text-xs font-bold text-white ${prop.tagColor}`}>
                      {prop.tag}
                    </span>
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg">{prop.address}</h3>
                  <p className="text-sm text-slate-500 mb-2">{prop.city}</p>
                  <div className="flex items-center gap-2 mb-3 text-sm">
                    <span className="font-bold text-green-700">{prop.price}</span>
                    <span className="text-gray-400">|</span>
                    <span className="text-gray-600">{prop.acres}</span>
                  </div>
                  <p className="text-sm text-slate-600 italic mb-4">
                    {prop.why}
                  </p>
                  {prop.link ? (
                    <Link
                      to={prop.link}
                      className="block w-full py-2 bg-slate-900 hover:bg-slate-800 text-white text-center rounded-lg text-sm font-semibold transition-colors"
                    >
                      View Details
                    </Link>
                  ) : (
                    <Link
                      to="/contact"
                      className="block w-full py-2 bg-amber-600 hover:bg-amber-700 text-white text-center rounded-lg text-sm font-semibold transition-colors"
                    >
                      Request Info
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== STEP 3: Full Inventory Table ===== */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {/* Context above table */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="text-center mb-5">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Full Palm Bay Inventory</h2>
              <p className="text-slate-600">
                {inventory.length}+ lots available below. If you want help narrowing this down, don't guess — <Link to="/contact" className="text-amber-600 font-semibold hover:underline">ask</Link>.
              </p>
            </div>

            {/* Palm Bay Unit Map + explanation */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 md:p-5 flex flex-col sm:flex-row items-center gap-5">
              <button
                onClick={() => setShowUnitMap(true)}
                title="Click to enlarge the Palm Bay unit map"
                className="shrink-0 rounded-lg overflow-hidden border-2 border-[#1a3a5c] hover:ring-2 hover:ring-amber-500 transition shadow-md cursor-pointer bg-white"
              >
                <img src="/images/palm-bay-unit-map.jpg" alt="Palm Bay unit map showing all Port Malabar units" className="w-60 h-60 md:w-72 md:h-72 object-contain bg-white" loading="lazy" />
                <span className="block bg-[#1a3a5c] text-white text-xs font-semibold py-1.5 px-2 text-center">Palm Bay Unit Map — click to enlarge</span>
              </button>
              <div className="text-sm text-slate-700 leading-relaxed">
                <p className="font-bold text-slate-900 mb-1">Click to enlarge and see where each unit is located.</p>
                <p>
                  Palm Bay's <strong>unit numbers work like Florida's counties.</strong> Just as saying
                  "Brevard County" tells you you're on Florida's east-central coast, a Palm Bay
                  <strong> unit number</strong> tells you where a lot sits within the city. For example, find
                  <strong> Unit 49</strong> on this map and you'll see it's in the <strong>southwest of Palm Bay</strong>.
                  So when a lot lists a unit number, this map shows you its part of town at a glance.
                </p>
                <button onClick={() => setShowUnitMap(true)} className="mt-2 inline-block text-amber-700 font-semibold underline">
                  Open the full unit map →
                </button>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-6 space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 flex shadow-md rounded-lg">
                <input
                  type="text"
                  placeholder="Search by ID, Unit, Block, Lot, or Address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 border border-r-0 bg-white rounded-l-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <button
                  type="button"
                  onClick={(e) => { const inp = e.currentTarget.parentElement.querySelector('input'); if (inp) inp.blur(); }}
                  className="flex items-center gap-2 px-6 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-r-lg transition-colors"
                  aria-label="Search"
                >
                  <Search className="w-5 h-5" />
                  Search
                </button>
              </div>
              <Button 
                onClick={downloadExcel}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download Excel
              </Button>
              <button
                onClick={() => setFavoritesOnly(v => !v)}
                data-testid="favorites-toggle"
                title="Show only lots you've saved"
                className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-shadow border ${favoritesOnly ? 'bg-red-600 text-white border-red-700' : 'bg-white'}`}
              >
                <Heart className={`w-5 h-5 ${favoritesOnly ? 'fill-current' : ''}`} />
                Favorites ({favorites.size})
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border"
              >
                <Filter className="w-5 h-5" />
                Filters {(filterUnit !== 'all' || zoningFilter.length || fluFilter.length || quadrantFilter.length || minAcres || maxAcres) ? '(Active)' : ''}
              </button>
            </div>

            {showFilters && (
              <div className="rounded-xl overflow-hidden shadow-md border border-[#c9dcef]">
                <div className="bg-[#1a3a5c] text-white font-semibold px-5 py-3 flex items-center gap-2">
                  <Filter className="w-5 h-5" /> Refine your search
                </div>
                <div className="bg-[#eef4fb] px-5 divide-y divide-[#dbe7f3]">
                  {/* Zoning */}
                  <div>
                    <button type="button" onClick={() => setShowZoning(v => !v)} className="w-full flex items-center justify-between py-3 font-semibold text-slate-800">
                      Zoning
                      <span className={`text-amber-600 transition-transform ${showZoning ? 'rotate-180' : ''}`}>▾</span>
                    </button>
                    {showZoning && (
                      <div className="pb-3 space-y-1">
                        {zoningOptions.map(([name, count]) => (
                          <label key={name} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-[#dfeaf7] cursor-pointer text-sm text-slate-700">
                            <input type="checkbox" className="w-4 h-4 accent-amber-600" checked={zoningFilter.includes(name)} onChange={() => toggleValue(zoningFilter, setZoningFilter, name)} />
                            {name}
                            <span className="ml-auto text-slate-400 text-xs">{count}</span>
                          </label>
                        ))}
                        <p className="text-xs text-slate-500 italic pl-2">No zoning set = Residential (single-family).</p>
                      </div>
                    )}
                  </div>
                  {/* FLU */}
                  <div>
                    <button type="button" onClick={() => setShowFlu(v => !v)} className="w-full flex items-center justify-between py-3 font-semibold text-slate-800">
                      FLU (Future Land Use)
                      <span className={`text-amber-600 transition-transform ${showFlu ? 'rotate-180' : ''}`}>▾</span>
                    </button>
                    {showFlu && (
                      <div className="pb-3 space-y-1">
                        {fluOptions.map(([name, count]) => (
                          <label key={name} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-[#dfeaf7] cursor-pointer text-sm text-slate-700">
                            <input type="checkbox" className="w-4 h-4 accent-amber-600" checked={fluFilter.includes(name)} onChange={() => toggleValue(fluFilter, setFluFilter, name)} />
                            {name}
                            <span className="ml-auto text-slate-400 text-xs">{count}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Quadrant Filter */}
                  <div>
                    <button type="button" onClick={() => setShowQuadrant(v => !v)} className="w-full flex items-center justify-between py-3 font-semibold text-slate-800">
                      Quadrant (NW, NE, SW, SE)
                      <span className={`text-amber-600 transition-transform ${showQuadrant ? 'rotate-180' : ''}`}>▾</span>
                    </button>
                    {showQuadrant && (
                      <div className="pb-3 space-y-1">
                        {quadrantOptions.length > 0 ? (
                          <>
                            {quadrantOptions.map(([name, count]) => (
                              <label key={name} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-[#dfeaf7] cursor-pointer text-sm text-slate-700">
                                <input type="checkbox" className="w-4 h-4 accent-amber-600" checked={quadrantFilter.includes(name)} onChange={() => toggleValue(quadrantFilter, setQuadrantFilter, name)} />
                                {name}
                                <span className="ml-auto text-slate-400 text-xs">{count}</span>
                              </label>
                            ))}
                            <p className="text-xs text-slate-500 italic pl-2 pt-1">Malabar Rd divides North/South • Minton Rd divides East/West</p>
                          </>
                        ) : (
                          <p className="text-sm text-slate-500 p-2">No lots with quadrant data available</p>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Filter by Unit */}
                  <div className="py-3">
                    <label className="block text-sm font-semibold mb-2 text-slate-800">Filter by Unit</label>
                    <select value={filterUnit} onChange={(e) => setFilterUnit(e.target.value)} className="w-full p-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500">
                      <option value="all">All Units</option>
                      {uniqueUnits.map(unit => (<option key={unit} value={unit}>Unit {unit}</option>))}
                    </select>
                  </div>
                  {/* Lot size range */}
                  <div className="py-3">
                    <label className="block text-sm font-semibold mb-2 text-slate-800">Lot size (acres)</label>
                    <div className="flex items-center gap-3">
                      <input type="number" step="0.01" min="0" value={minAcres} onChange={(e) => setMinAcres(e.target.value)} placeholder="e.g. 0.23" className="w-32 p-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                      <span className="text-slate-500">to</span>
                      <input type="number" step="0.01" min="0" value={maxAcres} onChange={(e) => setMaxAcres(e.target.value)} placeholder="e.g. 0.35" className="w-32 p-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>
                  </div>
                  {/* Sort by */}
                  <div className="py-3 flex items-center gap-3">
                    <label className="text-sm font-semibold text-slate-800 whitespace-nowrap">Sort by:</label>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="flex-1 p-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500">
                      <option value="unit">Unit number (low to high)</option>
                      <option value="block">Block (low to high)</option>
                      <option value="lot">Lot (low to high)</option>
                      <option value="sizeAsc">Size (smallest to largest)</option>
                      <option value="sizeDesc">Size (largest to smallest)</option>
                    </select>
                  </div>
                  {/* Clear all */}
                  <div className="py-3">
                    <button onClick={() => { setFilterUnit('all'); setZoningFilter([]); setFluFilter([]); setQuadrantFilter([]); setMinAcres(''); setMaxAcres(''); setSortBy('unit'); }} className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors">
                      Clear all filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <p className="text-sm text-slate-600 mb-4 bg-white p-3 rounded-lg shadow-sm">
            Showing {filteredInventory.length} of {inventory.length} lots
            <span className="ml-4 text-xs">
              (<span className="text-blue-600">{groupedInventory.waterSewer.length} City Water & Sewer</span> | 
              <span className="text-cyan-600 ml-1">{groupedInventory.waterOnly.length} City Water</span> | 
              <span className="text-amber-600 ml-1">{groupedInventory.wellSeptic.length} Well & Septic</span>)
            </span>
          </p>

          {/* Inventory Tables by Utility Type */}
          
          {/* City Water & Sewer Section */}
          {groupedInventory.waterSewer.length > 0 && (
            <div className="mb-8">
              <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg flex items-center gap-3">
                <Droplets className="w-6 h-6" />
                <div>
                  <h3 className="text-xl font-bold">City Water & Sewer</h3>
                  <p className="text-blue-100 text-sm">{groupedInventory.waterSewer.length} lots — Premium utility access, no well or septic needed</p>
                </div>
              </div>
              <div className="bg-white rounded-b-lg shadow-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {groupedInventory.waterSewer.map((item) => renderLotCard(item, 'blue', 'City Water & Sewer'))}
                </div>
              </div>
            </div>
          )}

          {/* City Water Only Section */}
          {groupedInventory.waterOnly.length > 0 && (
            <div className="mb-8">
              <div className="bg-cyan-600 text-white px-6 py-4 rounded-t-lg flex items-center gap-3">
                <Droplets className="w-6 h-6" />
                <div>
                  <h3 className="text-xl font-bold">City Water Only</h3>
                  <p className="text-cyan-100 text-sm">{groupedInventory.waterOnly.length} lots — City water available, septic required</p>
                </div>
              </div>
              <div className="bg-white rounded-b-lg shadow-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {groupedInventory.waterOnly.map((item) => renderLotCard(item, 'cyan', 'City Water Only'))}
                </div>
              </div>
            </div>
          )}

          {/* Well & Septic Section */}
          {groupedInventory.wellSeptic.length > 0 && (
            <div className="mb-8">
              <div className="bg-amber-600 text-white px-6 py-4 rounded-t-lg flex items-center gap-3">
                <Home className="w-6 h-6" />
                <div>
                  <h3 className="text-xl font-bold">Well & Septic</h3>
                  <p className="text-amber-100 text-sm">{groupedInventory.wellSeptic.length} lots — Well and septic required, most affordable options</p>
                </div>
              </div>
              <div className="bg-white rounded-b-lg shadow-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {groupedInventory.wellSeptic.map((item) => renderLotCard(item, 'amber', 'Well & Septic'))}
                </div>
              </div>
            </div>
          )}

          {/* No results message */}
          {filteredInventory.length === 0 && (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <p className="text-slate-600 text-lg">No lots found matching your search criteria.</p>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-6 p-6 bg-red-50 border-2 border-red-300 rounded-lg">
            <h3 className="text-lg font-bold text-red-900 mb-2">Important Disclaimer</h3>
            <p className="text-sm text-red-800 leading-relaxed">
              <strong>All prices are subject to verification and may change without notice.</strong> The information displayed on this page is for reference purposes only. Availability, pricing, and property details should be verified directly. Please contact Vahid Reza Rajabian at <a href="tel:3213337230" className="underline font-semibold">321-333-7230</a> or <a href="mailto:vahid@palmbayland.com" className="underline font-semibold">vahid@palmbayland.com</a> to verify current information and availability before making any purchase decisions.
            </p>
          </div>
        </div>
      </section>

      {/* ===== STEP 5: Authority Section at Bottom ===== */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-amber-400 mb-6">
              Why Buyers Use Me
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="flex flex-col items-center text-center">
                <CheckCircle className="w-8 h-8 text-amber-500 mb-2" />
                <p className="text-gray-300">20+ years focused on Palm Bay land</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <CheckCircle className="w-8 h-8 text-amber-500 mb-2" />
                <p className="text-gray-300">I explain what you can actually build — not just what zoning says</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <CheckCircle className="w-8 h-8 text-amber-500 mb-2" />
                <p className="text-gray-300">Trusted by builders, developers, and land investors</p>
              </div>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold transition-colors"
            >
              Request Parcel & Zoning Review
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Price Calculator Popup ===== */}
      {priceItem && (() => {
        const utilityType = getUtilityType(priceItem);
        const pricing = getLotPricing(priceItem, utilityType, canal);
        const lotLabel = [priceItem.streetNumber, priceItem.streetName].filter(Boolean).join(' ').trim()
          || `Unit ${priceItem.unit} Block ${priceItem.block} Lot ${priceItem.lot}`;
        return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={closePriceModal}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-slate-200">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{lotLabel}</h3>
                  <p className="text-sm text-slate-500">
                    Unit {priceItem.unit} · Block {priceItem.block} · Lot {priceItem.lot} · {priceItem.acres}
                  </p>
                </div>
                <button onClick={closePriceModal} className="p-1 hover:bg-slate-100 rounded-lg" aria-label="Close">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <div className="px-6 py-5">
                {pricing.status === 'package' && (
                  <div className="text-center py-4">
                    <p className="text-lg font-bold text-slate-900 mb-2">Not Sold Individually</p>
                    <p className="text-slate-600 text-sm mb-4">This lot is available only as part of a whole package. Seller financing is available.</p>
                    <a href="tel:3213337230" className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-sm">
                      <Phone className="w-4 h-4" /> Call 321-333-7230
                    </a>
                  </div>
                )}

                {pricing.status === 'call' && (
                  <div className="text-center py-4">
                    <p className="text-lg font-bold text-slate-900 mb-2">Call for Pricing</p>
                    <p className="text-slate-600 text-sm mb-4">This parcel is priced individually. Seller financing is available.</p>
                    <a href="tel:3213337230" className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-sm">
                      <Phone className="w-4 h-4" /> Call 321-333-7230
                    </a>
                  </div>
                )}

                {pricing.status === 'price' && (
                  <>
                    {/* Price */}
                    <div className="text-center mb-4">
                      <p className="text-sm text-slate-500 uppercase tracking-wide">Cash Price</p>
                      <p className="text-4xl font-extrabold text-green-700">{usd(pricing.price)}</p>
                    </div>

                    {/* Canal checkbox — only for calculator-priced lots, not hand-set prices */}
                    {!pricing.fixed && (
                      <label className="flex items-center gap-2 justify-center mb-5 text-sm text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={canal} onChange={(e) => setCanal(e.target.checked)} className="w-4 h-4 accent-amber-600" />
                        Canal lot (+$5,000)
                      </label>
                    )}

                    {pricing.cashOnly && (
                      <div className="text-center mb-2 bg-slate-100 rounded-lg p-3">
                        <p className="text-sm font-bold text-slate-900">Cash Only</p>
                        <p className="text-xs text-slate-500">Owner financing is not available on this listing.</p>
                      </div>
                    )}

                    {!pricing.cashOnly && (<>
                    {/* Financing options */}
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Owner Financing Options</p>
                    <div className="space-y-3">
                      <div className="border border-slate-200 rounded-xl p-4">
                        <div className="flex items-baseline justify-between">
                          <span className="font-bold text-slate-900">30% Option Money</span>
                          <span className="text-slate-900 font-semibold">{usd(pricing.down30)} option money</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Financing {usd(pricing.fin30)} at 10% fixed interest (12.33%–12.58% APR*)</p>
                        <p className="text-2xl font-bold text-amber-600 mt-1">{usd(pricing.monthly30)}<span className="text-sm font-medium text-slate-500">/mo · 120 months</span></p>
                        <p className="text-xs text-slate-600 mt-1 font-medium">Option Contract — deed transfers at 40% paid. <span className="text-amber-700">Financing charges and APR apply.</span> <a href="/owner-financing-land-florida#financing-disclosure" className="underline text-blue-600 hover:text-blue-700">View full terms.</a></p>
                      </div>
                      <div className="border border-slate-200 rounded-xl p-4">
                        <div className="flex items-baseline justify-between">
                          <span className="font-bold text-slate-900">40% Down</span>
                          <span className="text-slate-900 font-semibold">{usd(pricing.down40)} down</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Financing {usd(pricing.fin40)} at 10% fixed interest (12.33%–12.58% APR*)</p>
                        <p className="text-2xl font-bold text-amber-600 mt-1">{usd(pricing.monthly40)}<span className="text-sm font-medium text-slate-500">/mo · 120 months</span></p>
                        <p className="text-xs text-slate-600 mt-1 font-medium">Deed Transfer at closing. <span className="text-amber-700">Financing charges and APR apply.</span> <a href="/owner-financing-land-florida#financing-disclosure" className="underline text-blue-600 hover:text-blue-700">View full terms.</a></p>
                      </div>
                    </div>

                    {/* Custom down-payment calculator */}
                    {(() => {
                      const down = Math.round(pricing.price * (downPct / 100));
                      const financed = pricing.price - down;
                      const monthly = monthlyPayment(financed);
                      return (
                        <div className="mt-4 bg-slate-900 rounded-xl p-4 text-white">
                          <div className="flex items-baseline justify-between mb-2">
                            <span className="text-sm font-semibold text-amber-400">Put more down?</span>
                            <span className="text-sm font-bold">{downPct}% down</span>
                          </div>
                          <input
                            type="range" min="30" max="100" step="1"
                            value={downPct}
                            onChange={(e) => setDownPct(Number(e.target.value))}
                            className="w-full accent-amber-500"
                          />
                          <div className="flex justify-between text-[11px] text-slate-400 mb-3">
                            <span>30%</span><span>Paid in full</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <p className="text-[11px] text-slate-400">Option Money</p>
                              <p className="text-sm font-bold">{usd(down)}</p>
                            </div>
                            <div>
                              <p className="text-[11px] text-slate-400">Financing (principal only)</p>
                              <p className="text-sm font-bold">{usd(financed)}</p>
                            </div>
                            <div>
                              <p className="text-[11px] text-slate-400">Payment</p>
                              <p className="text-sm font-bold text-amber-400">{financed <= 0 ? '$0' : usd(monthly) + '/mo'}</p>
                            </div>
                          </div>
                          <p className="text-xs font-bold text-white mt-3 text-center">10% fixed interest rate (12.33%–12.58% APR*)</p>
                          <p className="text-[10px] text-amber-300 text-center mt-1"><strong>Financing charges & APR apply</strong></p>
                        </div>
                      );
                    })()}

                    {/* Required financing disclosure */}
                    <p className="text-[11px] leading-snug text-slate-500 mt-3">
                      Amortized up to 120 months at 10% fixed interest. A financing charge equal to 10% of the remaining principal balance applies at closing. Financing charges and APR apply — <a href="/owner-financing-land-florida#financing-disclosure" className="text-blue-600 underline hover:text-blue-700">see representative example and full financing terms</a>. No prepayment penalty, no balloon. Subject to credit approval and execution of appropriate legal documents. This is an example only and is not an offer of credit. Terms subject to change.
                    </p>

                    <Link
                      to="/price-guide?financing=1#option-contract"
                      onClick={closePriceModal}
                      className="mt-3 block text-center px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-sm"
                    >
                      What is an option contract?
                    </Link>
                    <Link to="/price-guide?financing=1" onClick={closePriceModal} className="block text-center text-xs text-amber-700 underline mt-2">
                      See Financing Terms
                    </Link>
                    </>)}

                    <a href="tel:3213337230" className="mt-5 flex items-center justify-center gap-2 px-5 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold text-sm">
                      <Phone className="w-4 h-4" /> Lock In This Lot — 321-333-7230
                    </a>
                    <p className="text-[11px] text-slate-400 text-center mt-3">Price and terms may change without notice.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== Satellite Parcel Map Popup ===== */}
      {mapItem && (
        <ParcelMapModal
          item={mapItem}
          onClose={() => setMapItem(null)}
          favorited={favorites.has(favId(mapItem))}
          onToggleFav={toggleFavorite}
        />
      )}

      {/* ===== Palm Bay Unit Map Lightbox ===== */}
      {showUnitMap && (
        <div className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4" onClick={() => setShowUnitMap(false)}>
          <div className="relative max-w-5xl w-full max-h-[92vh] overflow-auto bg-white rounded-lg" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowUnitMap(false)} aria-label="Close" className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1 shadow">
              <X className="w-6 h-6 text-slate-700" />
            </button>
            <img src="/images/palm-bay-unit-map.jpg" alt="City of Palm Bay unit map showing all Port Malabar units" className="w-full h-auto" />
            <div className="p-3 text-center border-t border-slate-200">
              <a href="/images/palm-bay-unit-map.jpg" target="_blank" rel="noopener noreferrer" className="text-sm text-amber-700 underline font-semibold">Open full size in a new tab</a>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Inventory;
